export type EventStreamStatus = 'idle' | 'connecting' | 'open' | 'closed'

export interface NotificationEvent<TData = Record<string, unknown>> {
	event_id: string
	event_type: string
	channel: string
	timestamp: string
	terminal: boolean
	data: TData
}

export interface EventStreamConnectionOptions {
	baseUrl: string
	channel: string
	token: string
	replay?: boolean
	withCredentials?: boolean
	enterpriseId?: string | undefined
}

export interface EventStreamSubscription<TData = Record<string, unknown>> {
	onEvent?: (event: NotificationEvent<TData>) => void
	onStatusChange?: (status: EventStreamStatus) => void
	onError?: (error: string | null) => void
}

type SubscriptionHandle = () => void

const CLOSED_RECONNECT_DELAY_MS = 1000

function normalizeBaseUrl(baseUrl: string): string {
	const trimmed = baseUrl.trim().replace(/\/$/, '')
	return trimmed.endsWith('/subscribe') ? trimmed : `${trimmed}/subscribe`
}

function buildSubscriptionUrl(options: EventStreamConnectionOptions): string {
	const params = new URLSearchParams({
		channel: options.channel,
		token: options.token,
	})

	if (options.replay) {
		params.set('replay', 'true')
	}

	if (options.enterpriseId) {
		params.set('enterprise_id', options.enterpriseId)
	}

	return `${normalizeBaseUrl(options.baseUrl)}?${params.toString()}`
}

function buildConnectionKey(options: EventStreamConnectionOptions): string {
	return JSON.stringify({
		baseUrl: normalizeBaseUrl(options.baseUrl),
		channel: options.channel,
		token: options.token,
		replay: options.replay ?? false,
		withCredentials: options.withCredentials ?? false,
		enterpriseId: options.enterpriseId ?? null,
	})
}

class EventStreamConnection<TData = Record<string, unknown>> {
	private readonly subscribers = new Set<EventStreamSubscription<TData>>()
	private eventSource: EventSource | null = null
	private reconnectTimer: ReturnType<typeof setTimeout> | null = null
	private status: EventStreamStatus = 'idle'
	private lastError: string | null = null

	constructor(
		private readonly options: EventStreamConnectionOptions,
		private readonly onEmpty: () => void,
	) {}

	subscribe(subscription: EventStreamSubscription<TData>): SubscriptionHandle {
		this.subscribers.add(subscription)
		subscription.onStatusChange?.(this.status)
		subscription.onError?.(this.lastError)

		if (!this.eventSource && !this.reconnectTimer) {
			this.connect()
		}

		return () => {
			this.subscribers.delete(subscription)
			if (this.subscribers.size === 0) {
				this.close()
				this.onEmpty()
			}
		}
	}

	close(): void {
		if (this.status === 'closed' && !this.eventSource && !this.reconnectTimer) {
			return
		}
		this.clearReconnectTimer()
		this.destroySource()
		this.setStatus('closed')
	}

	private connect(): void {
		if (this.eventSource || this.subscribers.size === 0) {
			return
		}

		if (typeof EventSource === 'undefined') {
			this.setError('EventSource is not available in this environment.')
			this.setStatus('closed')
			return
		}

		this.setError(null)
		this.setStatus('connecting')

		const source = new EventSource(buildSubscriptionUrl(this.options), {
			withCredentials: this.options.withCredentials ?? false,
		})
		this.eventSource = source

		source.onopen = () => {
			if (this.eventSource !== source) {
				return
			}
			this.setError(null)
			this.setStatus('open')
		}

		source.onerror = () => {
			if (this.eventSource !== source) {
				return
			}

			if (source.readyState === EventSource.CLOSED) {
				this.setError('Event stream closed. Reconnecting.')
				this.destroySource(source)
				this.scheduleReconnect()
				return
			}

			this.setError('Event stream interrupted. Waiting for automatic reconnect.')
			this.setStatus('connecting')
		}

		source.addEventListener('message', (messageEvent: MessageEvent) => {
			if (this.eventSource !== source) {
				return
			}

			try {
				const parsed = JSON.parse(messageEvent.data) as NotificationEvent<TData>
				for (const subscriber of Array.from(this.subscribers)) {
					subscriber.onEvent?.(parsed)
				}

				if (parsed.terminal) {
					this.close()
				}
			} catch {
				this.setError('Received a malformed SSE payload.')
			}
		})
	}

	private setStatus(status: EventStreamStatus): void {
		this.status = status
		for (const subscriber of Array.from(this.subscribers)) {
			subscriber.onStatusChange?.(status)
		}
	}

	private setError(error: string | null): void {
		this.lastError = error
		for (const subscriber of Array.from(this.subscribers)) {
			subscriber.onError?.(error)
		}
	}

	private scheduleReconnect(): void {
		if (this.reconnectTimer || this.subscribers.size === 0) {
			return
		}

		this.setStatus('connecting')
		this.reconnectTimer = setTimeout(() => {
			this.reconnectTimer = null
			this.connect()
		}, CLOSED_RECONNECT_DELAY_MS)
	}

	private clearReconnectTimer(): void {
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer)
			this.reconnectTimer = null
		}
	}

	private destroySource(expectedSource?: EventSource): void {
		if (!this.eventSource) {
			return
		}
		if (expectedSource && this.eventSource !== expectedSource) {
			return
		}

		this.eventSource.close()
		this.eventSource = null
	}
}

export class NotificationEventStreamManager {
	private readonly connections = new Map<string, EventStreamConnection<any>>()

	subscribe<TData = Record<string, unknown>>(
		options: EventStreamConnectionOptions,
		subscription: EventStreamSubscription<TData>,
	): SubscriptionHandle {
		const key = buildConnectionKey(options)
		let connection = this.connections.get(key) as EventStreamConnection<TData> | undefined

		if (!connection) {
			connection = new EventStreamConnection<TData>(options, () => {
				this.connections.delete(key)
			})
			this.connections.set(key, connection as EventStreamConnection<any>)
		}

		return connection.subscribe(subscription)
	}

	closeAll(): void {
		for (const connection of this.connections.values()) {
			connection.close()
		}
		this.connections.clear()
	}
}

export const notificationEventStreamManager = new NotificationEventStreamManager()
